import React from 'react';
import { __ } from '@wordpress/i18n';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

import { TAXONOMY_MAP } from 'config/prop-types';

import IconRemoveX from 'images/icon-remove-x.svg';
import IconSearch from 'images/icon-search-gray.svg';

import './grantsFilters.scss';

export default class GrantsFilters extends React.PureComponent {
  /**
   * Takes a list of strings and turns them into an array of objects.
   * @param {array} list - values, either strings or objects.
   * @param {string} key - if the list has objects, define the key to use.
   * @returns {array} - The formatted list.
   */
  static formatListForDropdowns(list, labelKey, valueKey) {
    const formattedList = list.map((item) => ({
      label: labelKey ? item[labelKey] : item,
      value: valueKey ? item[valueKey] : item,
    }));
    return formattedList;
  }

  /**
   * Takes an array and sorts by label.
   * @param {array} list - values, either strings or objects.
   * @returns {array} - The formatted list, sorted by label.
   */
  static sortListforDropdowns(list) {
    return list.sort((
      { label: labelA },
      { label: labelB }
    ) => labelA.localeCompare(labelB));
  }

  /**
   * Constructor. Binds function scope.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      searchString: undefined,
      filterToggleOpen: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleFilters = this.toggleFilters.bind(this);
  }

  /**
   * Dispatches a filter event.
   * @param {string} source - key or undefined (ex. button click).
   * @param {object} event - JS event object.
   */
  handleSubmit(source, event) {
    if (('key' === source ? 'Enter' === event.key : true) &&
      (this.state.searchString &&
        '' !== this.state.searchString)
    ) {
      this.props.onFilter('Search', this.state.searchString);
    }
  }

  toggleFilters() {
    this.setState({ filterToggleOpen: ! this.state.filterToggleOpen });
  }

  /**
   * Utility function to determine if there are any filters set.
   * @returns {integer} - The number of filters set.
   */
  hasFilters() {
    const {
      activeType,
      activeProgram,
      activeSearch,
      activeState,
      activeStrategy,
      activeYear,
    } = this.props;

    return [
      ...activeType,
      ...activeProgram,
      ...activeSearch,
      ...activeState,
      ...activeStrategy,
      ...activeYear,
    ].length;
  }

  /**
   * Renders a filter dropdown with appropriate markup and elements.
   * @param {string} selected - The selected option.
   * @param {object} options - formatted list for Dropdown library to render.
   * @param {string} type - the filter's type.
   * @returns {object} - JSX for the dropdown.
   */
  renderFilterDropdown(selected, options, type, slug) {
    const { onFilter } = this.props;
    const isDisabled = false;
    const stateClass = isDisabled ? 'disabled' : 'enabled';
    const typeSlug = slug || type;

    return (
      <div
        className={classNames(
          'grants-listing__filter',
          type.replace(/\s+/g, '-').toLowerCase(),
          stateClass,
          'form-group',
        )}
      >
        { /**
         * Manually override the rendering of the "Program" label as "Area", per KORG-1450 and KORG-1473.
         */}
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label htmlFor={`dropdown${typeSlug}`}>
          {__('Program' === typeSlug ? 'Area' : typeSlug, 'kauffman')}
        </label>
        <div className="Dropdown-root">
          <select
            className="form-control Dropdown-control"
            id={`dropdown${typeSlug}`}
            name={`fs[${typeSlug.replace(/\s+/g, '-').toLowerCase()}]`}
            onChange={(evt) => {
              if (evt.target.value) {
                onFilter(typeSlug, parseInt(evt.target.value, 10));
                evt.target.selectedIndex = 0;
              }
            }}
          >
            <option
              disabled={isDisabled}
              value=""
            >
              {__(`Select ${type}`, 'kauffman')}
            </option>
            {options.map(({ label, value }) => (
              <option
                disabled={isDisabled}
                value={value}
              >
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  /**
   * Creates the markup for the display of active filters.
   * @param {array} filters - active filters.
   * @param {type} type - the type of the filter (ex. Program, Strategy, etc)
   * @param {array} taxonomyCollection - the collection of taxonomy terms that correspond to the filter type
   * @returns {object} JSX - tag markup.
   */
  renderFilterCategoryTags(filters, type, taxonomyCollection = null) {
    const {
      removeFilter,
    } = this.props;
    return (
      filters.map((filter) => {
        let displayName = filter;
        // Need to look up the string name for taxonomy filters based on id
        if (taxonomyCollection) {
          displayName = taxonomyCollection[filter].name;
        }
        return (
          <li key={filter}>
            <div>{displayName}</div>
            <button
              aria-label={`Remove filter ${filter}`}
              onClick={() => removeFilter(type, filter)}
            >
              <img src={IconRemoveX} alt="x" />
            </button>
          </li>
        );
      })
    );
  }

  /**
   * Renders component markup.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      activeType,
      activeProgram,
      activeSearch,
      activeState,
      activeStrategy,
      activeYear,
      grantTypesTaxonomy,
      grantTypesFiltered,
      programAreasTaxonomy,
      statesTaxonomy,
      statesFiltered,
      strategiesTaxonomy,
      strategiesFiltered,
      years,
    } = this.props;

    const formattedStrategies =
      GrantsFilters.formatListForDropdowns(Object
        .values(strategiesFiltered), 'name', 'id');
    const sortedStrategies =
      GrantsFilters.sortListforDropdowns(formattedStrategies);
    const formattedStates = GrantsFilters
      .formatListForDropdowns(Object.values(statesFiltered), 'name', 'id')
      .sort((
        { label: labelA = '' },
        { label: labelB = '' }
      ) => labelA.localeCompare(labelB));
    const formattedGrantTypes = GrantsFilters
      .formatListForDropdowns(Object.values(grantTypesFiltered), 'name', 'id')
      .sort((
        { label: labelA = '' },
        { label: labelB = '' }
      ) => labelA.localeCompare(labelB));
    const formattedYears =
      GrantsFilters.formatListForDropdowns(years, undefined);

    return (
      <div className="grants-listing__filters">
        <div className="grants-listing__filters-container">
          <div className="grants-listing__header">
            <h2 className="grants-listing__heading">
              {__('Search all grants:', 'kauffman')}
            </h2>
            { /* Search input */ }
            {/* eslint-disable-next-line max-len */}
            <div className="grants-listing__input search grants-app__mobile-only">
              {/* eslint-disable-next-line jsx-a11y/label-has-for */}
              <label htmlFor="searchSelect">
                {__('Search', 'kauffman')}
              </label>
              <div className="search-input-wrap">
                <input
                  id="searchSelect"
                  className="grants__text-input"
                  onChange={(event) =>
                    this.setState({ searchString: event.target.value })}
                  onKeyDown={(event) => this.handleSubmit('key', event)}
                  placeholder={
                    __('Search name, location, and details', 'kauffman')}
                  type="text"
                  value={DOMPurify.sanitize(this.state.searchString)}
                />
                <button onClick={this.handleSubmit} >
                  <img
                    src={IconSearch}
                    alt={__('Submit search', 'kauffman')}
                  />
                </button>
              </div>
            </div>
            {/* eslint-disable-next-line max-len */}
            <button
              className="grants-listing__filters-toggle grants-app__mobile-only"
              type="button"
              onClick={this.toggleFilters}
              aria-controls="grants-app-filters"
              aria-expanded={this.state.filterToggleOpen}
              id="grants-app-filter-toggle"
            >
              {__('Filter by:', 'kauffman')}
            </button>
            { /* Filter and search controls */ }
            <div
              className={
                classNames(
                  'grants-listing__filters-wrap',
                  this.state.filterToggleOpen ? 'is-active' : '',
                )
              }
              id="grants-app-filters"
              aria-labelledby="grants-app-filter-toggle"
              role="region"
            >
              { /* Search input */ }
              {/* eslint-disable-next-line max-len */}
              <div className="grants-listing__input search grants-app__desktop-only">
                {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                <label htmlFor="searchSelect">
                  {__('Search', 'kauffman')}
                </label>
                <div className="search-input-wrap">
                  <input
                    id="searchSelect"
                    className="grants__text-input"
                    onChange={(event) =>
                      this.setState({ searchString: event.target.value })}
                    onKeyDown={(event) => this.handleSubmit('key', event)}
                    placeholder={
                      __('Search name, location, and details', 'kauffman')}
                    type="text"
                    value={DOMPurify.sanitize(this.state.searchString)}
                  />
                  <button onClick={this.handleSubmit} >
                    <img
                      src={IconSearch}
                      alt={__('Submit search', 'kauffman')}
                    />
                  </button>
                </div>
              </div>
              { /* Dropdowns */ }
              {this.renderFilterDropdown(
                activeState,
                formattedStates,
                __('State', 'kauffman')
              )}
              {this.renderFilterDropdown(
                activeYear,
                formattedYears,
                __('Year', 'kauffman')
              )}
              {this.renderFilterDropdown(
                activeStrategy,
                sortedStrategies,
                __('Strategic Priority', 'kauffman'),
                'Strategy',
              )}
              {this.renderFilterDropdown(
                activeType,
                formattedGrantTypes,
                __('Grant Type', 'kauffman'),
                'Type'
              )}
            </div>
          </div>
          { /* Applied filter tags */ }
          <div className="grants-listing__applied-filters">
            {!! this.hasFilters() && (
              <ul className="grants-listing__applied-filters-list">
                {this.renderFilterCategoryTags(
                  activeProgram,
                  'Program',
                  programAreasTaxonomy
                )}
                {this.renderFilterCategoryTags(
                  activeStrategy,
                  'Strategy',
                  strategiesTaxonomy
                )}
                {this.renderFilterCategoryTags(
                  activeState,
                  'State',
                  statesTaxonomy
                )}
                {this.renderFilterCategoryTags(
                  activeType,
                  'Type',
                  grantTypesTaxonomy
                )}
                {this.renderFilterCategoryTags(activeYear, 'Year')}
                {this.renderFilterCategoryTags(activeSearch, 'Search')}
                <li>
                  <button
                    className="grants-listing__clear-filters"
                    onClick={() => {
                      this.setState({ searchString: '' });
                      this.props.clearFilters();
                    }}
                  >
                    {__('Clear All Filters', 'kauffman')}
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }
}

GrantsFilters.propTypes = {
  activeType: PropTypes.arrayOf(PropTypes.number).isRequired,
  activeProgram: PropTypes.arrayOf(PropTypes.number).isRequired,
  activeSearch: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeState: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStrategy: PropTypes.arrayOf(PropTypes.number).isRequired,
  activeYear: PropTypes.arrayOf(PropTypes.string).isRequired,
  clearFilters: PropTypes.func.isRequired,
  grantTypesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  grantTypesFiltered: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  onFilter: PropTypes.func.isRequired,
  programAreasTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  programsFiltered: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  removeFilter: PropTypes.func.isRequired,
  strategiesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  strategiesFiltered: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  statesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  statesFiltered: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  years: PropTypes.arrayOf(PropTypes.string).isRequired,
};
